import React from "react";
import compose from "../../../utils/compose";

import { connect } from "react-redux";
import { changeReprCmpxGraphSettings, prepareDataForSave } from "../../../redux/actions/report-actions";

import withLocalization from "../../../hoc/with-localization";

import getSubfragm from "../../../graphics/get-subfragm";
import withSignalGetter from "../../../hoc/with-signal-getter";
import { Spinner /*ErrorIndicator*/ } from "../../state-indicators";

import ECGGraphSVG, { reprCmpxGraphSettings } from "../../../graphics/svg/ecg-graph-svg";
import { MagnifierY } from "../../magnifier";
import getDiffProperties from "../../../utils/getDiffProperties";
import { parseSignal_LeadsInSeparateArrays } from "../../../graphics/graph-funcs";

import "./graph-representative-cmpx.scss";

class GraphRepresentativeCmpx extends React.Component {
    _mounted = false;
    state = {
        loading: true,
        error: false,

        zoomYInDisabled: false,
        zoomYOutDisabled: false
    };

    yScales = [5, 10, 20, 40];

    componentDidMount() {
        const {
            scale: { voltageScale_mm_mV }
        } = this.props;
        const zoomDisabled = {
            zoomYInDisabled: voltageScale_mm_mV >= this.yScales[this.yScales.length - 1],
            zoomYOutDisabled: voltageScale_mm_mV <= this.yScales[0]
        };
        this.setState(zoomDisabled);
        this._mounted = true;
        this.drawerECGGraphSVG = new ECGGraphSVG("ReprCmpxGraph");
        this.drawGraph();
    }
    componentWillUnmount() {
        this._mounted = false;
    }

    componentDidUpdate(prevProps, prevState) {
        const {
            scale: { timeScale_mm_s: timeScale_mm_s_p, voltageScale_mm_mV: voltageScale_mm_mV_p },
            Q: Q_p,
            S: S_p,
            P: P_p,
            QTmax: QTmax_p,
            editedIntervals: editedIntervals_p,
            signalFilters: filters_p,
            lang: lang_p
        } = prevProps;
        const { Q: Q_edited_p, S: S_edited_p, P: P_edited_p, PDur: PDur_edited_p, QTmax: QTmax_edited_p } = editedIntervals_p ?? {};

        const {
            scale: { timeScale_mm_s, voltageScale_mm_mV },
            Q,
            S,
            P,
            QTmax,
            editedIntervals,
            areasInsteadTicks,
            signalFilters: filters,
            lang
        } = this.props;
        const { Q: Q_edited, S: S_edited, P: P_edited, PDur: PDur_edited, QTmax: QTmax_edited } = editedIntervals ?? {};

        if (Q === null || Q === undefined) {
            this.drawSVG();
            return;
        }

        // Redraw svg graph
        if (
            timeScale_mm_s_p !== timeScale_mm_s ||
            voltageScale_mm_mV_p !== voltageScale_mm_mV ||
            Q_p !== Q ||
            S_p !== S ||
            P_p !== P ||
            QTmax_p !== QTmax ||
            lang_p !== lang ||
            JSON.stringify(filters) !== JSON.stringify(filters_p)
        ) {
            this.drawGraph();
        }

        // Update areas
        const { start, length } = this.state;
        const { start: start_p, length: length_p } = prevState;
        if (areasInsteadTicks && editedIntervals) {
            if (
                start !== start_p ||
                length !== length_p ||
                Q_edited !== Q_edited_p ||
                S_edited !== S_edited_p ||
                P_edited !== P_edited_p ||
                PDur_edited !== PDur_edited_p ||
                QTmax_edited !== QTmax_edited_p
            ) {
                this.drawAreas();
            }
        }

        if (timeScale_mm_s_p !== timeScale_mm_s)
            this.setState({
                zoomXInDisabled: timeScale_mm_s >= this.xScales[this.xScales.length - 1],
                zoomXOutDisabled: timeScale_mm_s <= this.xScales[0]
            });

        if (voltageScale_mm_mV_p !== voltageScale_mm_mV)
            this.setState({
                zoomYInDisabled: voltageScale_mm_mV >= this.yScales[this.yScales.length - 1],
                zoomYOutDisabled: voltageScale_mm_mV <= this.yScales[0]
            });
    }

    drawAreas() {
        // Draw areas instead ticks

        if (!this.elSVG) return;

        const { signal } = this.props;
        // Clear ticks
        const elTicks = this.elSVG.querySelectorAll(`g[id^="ReprCmpxGraph_Ticks_"], g[id^="ReprCmpxGraph_Areas_"]`);
        elTicks.forEach(_el => _el.remove());

        // Draw areas
        const { start, length } = this.state;
        if (start === null || start === undefined) return;

        let { editedIntervals: { Q, S, P, PDur, QTmax } = {} } = this.props;

        const { ms_pnt } = this;
        if (Q !== null && Q !== undefined) {
            Q = ms_pnt(Q);
            Q -= start;

            if (S !== null && S !== undefined) S = ms_pnt(S);
            if (P !== null && P !== undefined) P = ms_pnt(P);
            if (QTmax !== null && QTmax !== undefined) QTmax = ms_pnt(QTmax);
            if (PDur !== null && PDur !== undefined) PDur = ms_pnt(PDur);

            QTmax = QTmax === null || QTmax === undefined ? QTmax : QTmax + Q;
            S = S === null || S === undefined ? S : S + Q;
            P = P === null || P === undefined ? P : P + Q;
        }

        const areas = [{ start: P, dur: PDur }, { start: Q, dur: S - Q }, { start: QTmax }];
        this.drawerECGGraphSVG.drawAreas(this.elSVG, areas, signal.fd, length);
    }
    ms_pnt = val => (val === undefined || val === null || val === "" ? null : Math.round((val * this.props.signal.fd) / 1000));
    drawGraph = () => {
        // Data
        const {
            scale: { timeScale_mm_s },
            signal: { signalLength, fd, channelNames },
            changeSettings,
            prepared
        } = this.props;
        let {
            Q,
            S,
            P,
            QTmax,
            scale: { desiredCenter_pnt }
        } = this.props;

        const { ms_pnt } = this;
        Q = ms_pnt(Q);
        S = ms_pnt(S);
        P = ms_pnt(P);
        QTmax = ms_pnt(QTmax);

        if (Q === null || Q === undefined || !prepared) {
            this.drawSVG();
            this.setState({ error: false, loading: false });
            return;
        }

        // Fragment start length
        const length50mm = (50 * fd) / timeScale_mm_s; // Ширина графика 40 мм
        // Запоминать положение графика, не то, он постоянно будет смещаться относительно центра QRS
        if (desiredCenter_pnt === null) {
            desiredCenter_pnt = Q + S / 2;
            changeSettings({ desiredCenter_pnt });
        }

        const { start, length } = getSubfragm(signalLength, Math.round(desiredCenter_pnt), length50mm);

        // Get signal
        this.props
            .reqSignal(start, length)
            .then(_data => {
                if (!this._mounted) {
                    return;
                }
                let data = [];

                data = parseSignal_LeadsInSeparateArrays(_data.signal, 1000);

                const existQTmax = !(QTmax === null || QTmax === undefined);
                Q = Q === null || Q === undefined ? Q : Q - start;
                if (Q !== null && Q !== undefined) {
                    QTmax = !existQTmax ? QTmax : QTmax + Q;
                    S = S === null || S === undefined ? S : S + Q;
                    P = P === null || P === undefined ? P : P + Q;
                }

                const signal = {
                    data_mV: data,
                    fd,
                    chQty: 12,
                    ptsQty: data.length / 12,
                    channelNames
                };

                const ticks = [{ X_pts: [Q, S, P, QTmax] }];

                if (this.svgcontainer) {
                    this.drawSVG({ signal, ticks });
                }

                this.setState({ start, length, error: false, loading: false });
            })
            .catch(error => {
                if (!this._mounted) {
                    return;
                }
                if (this.svgcontainer) {
                    this.svgcontainer.innerText = "";
                    this.drawSVG();
                }

                this.setState({ error: true, loading: false });
                console.error(error);
            });
    };

    drawSVG = ({ signal = null, ticks = null } = {}) => {
        const {
            loc,
            scale: { timeScale_mm_s, voltageScale_mm_mV },
            mode,

            signalFilters
        } = this.props;
        this.svgcontainer.innerText = "";
        const setts = {
            graphSetts: { ...reprCmpxGraphSettings.graphSetts, heightAdd_mm: 5 },
            gridSetts: [{ ...reprCmpxGraphSettings.gridSetts[0] }, { ...reprCmpxGraphSettings.gridSetts[1] }],
            plotSetts: { ...reprCmpxGraphSettings.plotSetts }
        };
        setts.plotSetts.ScaleX_mm_sec = timeScale_mm_s;
        setts.plotSetts.ScaleY_mm_mV = voltageScale_mm_mV;

        if (mode === "PopupDialog") {
            setts.plotSetts.channelHeight_mm = 25;
            setts.graphSetts.height_mm = 151;
            setts.gridSetts[0].rows = 15;
            setts.gridSetts[1].rows = 15;
        }

        const filterStrings = {
            baseline: loc("buttons.baseline"),
            _35Hz: "35 " + loc("units.Hz"),
            _50Hz: "50 " + loc("units.Hz"),
            spikes: loc("buttons.stimulusFilter")
        };
        const filterLabel = Object.keys(signalFilters)
            .filter(key => signalFilters[key])
            .map(key => filterStrings[key])
            .join(", ");
        const H = setts.graphSetts.height_mm;
        const labels = {
            X_pts: [0],
            Y_mm: [H + 3],
            texts: [loc("report.labels.filters") + " " + filterLabel],
            anchor: ["start"],
            font: "monospace",
            onlyOnFirstGrid: true
        };

        this.drawerECGGraphSVG.setSettings(setts);
        this.elSVG = this.drawerECGGraphSVG.draw({ signal, labels, ticks, time_scale_str: loc("units.mm_sec") });
        this.svgcontainer.appendChild(this.elSVG);
    };

    prepareZoomChangesSaving = sett => {
        const {
            prepareDataForSave,
            scale: { timeScale_mm_s, voltageScale_mm_mV }
        } = this.props;
        let prepared = {};
        let { diff, same } = getDiffProperties(
            { graphReprCmpx_timeScale_mm_s: timeScale_mm_s, graphReprCmpx_voltageScale_mm_mV: voltageScale_mm_mV },
            sett
        );
        diff.forEach(({ key, val }) => {
            if (val !== undefined) prepared[key] = val;
        });
        prepareDataForSave({ diff: prepared, same });
    };

    yZoomIn = () => {
        const {
            changeSettings,
            scale: { voltageScale_mm_mV }
        } = this.props;
        const newScale = this.yScales.find(_v => _v > voltageScale_mm_mV);

        if (newScale) {
            changeSettings({
                voltageScale_mm_mV: newScale
            });
            this.prepareZoomChangesSaving({
                graphReprCmpx_voltageScale_mm_mV: newScale
            });
        }
    };
    yZoomOut = () => {
        const {
            changeSettings,
            scale: { voltageScale_mm_mV }
        } = this.props;
        const newScale = this.yScales
            .slice()
            .reverse()
            .find(_v => _v < voltageScale_mm_mV);
        if (newScale) {
            changeSettings({
                voltageScale_mm_mV: newScale
            });
            this.prepareZoomChangesSaving({
                graphReprCmpx_voltageScale_mm_mV: newScale
            });
        }
    };
    render() {
        const { openDialog, popupable } = this.props;
        const { loading, error, zoomYInDisabled, zoomYOutDisabled } = this.state;
        const { yZoomIn, yZoomOut } = this;

        return (
            <div className={"report-graph report-graph-repr-cmpx" + (error ? " error" : "") + (popupable ? " popup-able" : "")}>
                <div
                    ref={_self => {
                        this.svgcontainer = _self;
                    }}
                    style={{ textAlign: "center" }}
                />
                {popupable ? (
                    <button className="popup-open-btn" data-popup-window="wholeGraph-popup-window" onClick={openDialog}>
                        &#xE800;
                    </button>
                ) : null}
                <MagnifierY zoomIn={yZoomIn} zoomOut={yZoomOut} disableOut={zoomYOutDisabled} disableIn={zoomYInDisabled} />
                {/* <div style={{ height: "100%", width: "100%" }} ref={_self => this.canvasWrap = _self}/> */}

                {
                    // error ? <ErrorIndicator transparent withCaption small {...{ errorCaptionKey: "errors.dataUnavailable" }} /> :
                    loading ? <Spinner small transparent /> : null
                }
            </div>
        );
    }
}

const mapStateToProps = ({
    reportApp: {
        reportData: {
            report: { representativeCmpx, signal },
            settings: {
                graphs: { graphReprCmpx: scale, signalFilters }
            }
        }
    }
}) => {
    let Q, S, P, QTmax;
    if (representativeCmpx) ({ Q, S, P, QTmax } = representativeCmpx);
    return {
        scale,
        Q: Q?.val,
        S: S?.val,
        P: P?.val,
        QTmax: QTmax?.val,
        signal,
        signalFilters
    };
};
const mapDispathToProps = {
    changeSettings: changeReprCmpxGraphSettings,
    prepareDataForSave
};

export default compose(withLocalization, withSignalGetter, connect(mapStateToProps, mapDispathToProps))(GraphRepresentativeCmpx);
